import React, { useCallback, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { BookOpenIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { ErrorBoundary, ToastContainer } from './components/ErrorBoundary'
import HeroSection from './components/HeroSection'
import MenuGrid from './components/MenuGrid'
import DashboardHome from './components/DashboardHome'
import ThemeToggle from './components/ThemeToggle'
import GlobalProgressBar from './components/GlobalProgressBar'
import ProgressProvider from './components/ProgressProvider'
import TelegramLayout from './components/TelegramLayout'
import WebLayout from './components/WebLayout'
import AppLayout from './components/AppLayout'
import FeedRightSidebar from './components/sidebars/FeedRightSidebar'
import ProfileRightSidebar from './components/sidebars/ProfileRightSidebar'
import NetworkRightSidebar from './components/sidebars/NetworkRightSidebar'
import CoursesRightSidebar from './components/sidebars/CoursesRightSidebar'
import JobsRightSidebar from './components/sidebars/JobsRightSidebar'
import WorkspaceRightSidebar from './components/sidebars/WorkspaceRightSidebar'
import DailyQuizRightSidebar from './components/sidebars/DailyQuizRightSidebar'
import AuthGuard from './components/AuthGuard'
import NotificationToast from './components/NotificationToast'
import EmailLinkPrompt from './components/EmailLinkPrompt'
import AppDownloadModal from './components/AppDownloadModal'
import { AuthProvider, useAuth } from './context/AuthContext'
import RoleGuard from './components/RoleGuard'
import { usePlatform } from './hooks/usePlatform'
import { useTelegramBackButton } from './hooks/useTelegramWebApp'

// ── Route-level code splitting ───────────────────────────────────────────────
// All page-level components are lazy-loaded to minimize the initial JS bundle.
// Only above-the-fold components (HeroSection, MenuGrid, DashboardHome, layouts)
// are eagerly imported.

const PageSkeleton = () => (
  <div className="w-full animate-pulse px-6 pt-8 pb-16 space-y-5">
    <div className="h-6 w-48 bg-slate-200 dark:bg-slate-800 rounded-lg" />
    <div className="h-4 w-72 bg-slate-100 dark:bg-slate-800/60 rounded-lg" />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pt-2">
      {[1,2,3].map(i => (
        <div key={i} className="h-48 rounded-2xl bg-slate-100 dark:bg-slate-800/50" />
      ))}
    </div>
  </div>
)

const LoginPage               = lazy(() => import('./pages/LoginPage'))
const VerifyEmailPendingPage  = lazy(() => import('./pages/VerifyEmailPendingPage'))
const VerifyEmailPage         = lazy(() => import('./pages/VerifyEmailPage'))
const ForgotPasswordPage      = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage       = lazy(() => import('./pages/ResetPasswordPage'))
const DailyQuizPage = lazy(() => import('./pages/DailyQuizPage'))
const KitoblarPage = lazy(() => import('./pages/KitoblarPage'))
const BookDetailPage = lazy(() => import('./pages/BookDetailPage'))
const BookReaderPage = lazy(() => import('./pages/BookReaderPage'))
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const DonationPage = lazy(() => import('./pages/DonationPage'))
const TermsPage          = lazy(() => import('./pages/TermsPage'))
const PrivacyPage        = lazy(() => import('./pages/PrivacyPage'))
const DeleteAccountPage  = lazy(() => import('./pages/DeleteAccountPage'))
const AdminPage = lazy(() => import('./pages/AdminPage'))
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'))
const BookSummarizerPage = lazy(() => import('./pages/BookSummarizerPage'))
const AICompanionPage = lazy(() => import('./pages/AICompanionPage'))
const TeacherDashboardPage = lazy(() => import('./pages/TeacherDashboardPage'))
const TeacherApplyPage = lazy(() => import('./pages/TeacherApplyPage'))
const TeacherProfileSetupPage = lazy(() => import('./pages/TeacherProfileSetupPage'))
const TeachersGalleryPage = lazy(() => import('./pages/TeachersGalleryPage'))
const CoursesPage = lazy(() => import('./pages/CoursesPage'))
const CourseDetailPage = lazy(() => import('./pages/CourseDetailPage'))
const CourseCreatePage = lazy(() => import('./pages/CourseCreatePage'))
const LessonCreatePage = lazy(() => import('./pages/LessonCreatePage'))
const TeacherWalletPage = lazy(() => import('./pages/TeacherWalletPage'))
const AdminPayoutsPage = lazy(() => import('./pages/AdminPayoutsPage'))
const AdminDailyQuizPage = lazy(() => import('./pages/AdminDailyQuizPage'))
const AdminCuratedFactsPage = lazy(() => import('./pages/AdminCuratedFactsPage'))
const SocialFeed = lazy(() => import('./pages/SocialFeed'))
const PublicProfile = lazy(() => import('./pages/ProfilePage'))
const DiscoverUsers = lazy(() => import('./pages/DiscoverUsers'))
const SearchPage = lazy(() => import('./pages/SearchPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const WorkspacePage = lazy(() => import('./pages/WorkspacePage'))
const NetworkPage = lazy(() => import('./pages/NetworkPage'))
const JobsPage = lazy(() => import('./pages/JobsPage'))
const FlashcardsPage = lazy(() => import('./pages/FlashcardsPage'))
const FlashcardDeckPage = lazy(() => import('./pages/FlashcardDeckPage'))
const FlashcardStudyPage = lazy(() => import('./pages/FlashcardStudyPage'))
const FlashcardCreatePage = lazy(() => import('./pages/FlashcardCreatePage'))
const FlashcardPublicDetailPage = lazy(() => import('./pages/FlashcardPublicDetailPage'))
const CertificatePage = lazy(() => import('./pages/CertificatePage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const SavedPostsPage = lazy(() => import('./pages/SavedPostsPage'))

/** Redirect legacy /teacher/:id to /profile/:id?tab=courses */
const TeacherRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/profile/${id}?tab=courses`} replace />
}

const HomePage: React.FC = () => {
  const { user } = useAuth()
  const { isTelegram } = usePlatform()

  // Web mode: premium dashboard (sidebar already handles navigation)
  if (!isTelegram) return <DashboardHome />

  return (
    <main className={`mx-auto pt-6 px-5 pb-10 paper-texture ${isTelegram ? 'max-w-md pb-28' : 'max-w-6xl'}`}>

      {/* ── Desktop two-column layout ───────────────────────────────────── */}
      <div className="lg:flex lg:gap-12 lg:items-start">

        {/* Left column: header + hero (desktop) or full width (Telegram/mobile) */}
        <div className="lg:w-72 lg:flex-shrink-0 lg:sticky lg:top-4">

          {/* Header — hidden in web (sidebar already shows logo) */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className={`mb-6 ${!isTelegram ? 'lg:hidden' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-sahifa-500 text-white flex items-center justify-center shadow-[0_10px_24px_rgba(255,106,42,0.18)]">
                  <BookOpenIcon className="w-5 h-5" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold tracking-[-0.03em] text-gray-900 dark:text-white">
                    SAHIFALAB
                  </h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Premium learning workspace</p>
                </div>
              </div>
              <ThemeToggle />
            </div>
          </motion.div>

          {/* Welcome card — always visible */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="mb-6"
          >
            <div className="speech-bubble px-4 py-3">
              <div className="flex items-center gap-2 mb-2 text-sahifa-500">
                <SparklesIcon className="w-4 h-4" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">Dashboard</span>
              </div>
              <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed font-medium">
                {user?.first_name
                  ? `Assalomu alaykum, ${user.first_name}!`
                  : 'Assalomu alaykum!'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Fokus, kurslar va bilim — bitta tartibli ish maydonida.
              </p>
            </div>
          </motion.div>

          {/* Hero — below welcome on mobile, sidebar-pinned on desktop */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <HeroSection />
          </motion.div>
        </div>

        {/* Right column: Menu Grid */}
        <div className="flex-1 min-w-0 mt-6 lg:mt-0">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Section label on desktop */}
            <p className="hidden lg:block text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500 mb-3">
              Asosiy bo'limlar
            </p>
            <MenuGrid />
          </motion.div>
        </div>
      </div>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-12 text-center space-y-1.5"
      >
        <div className="w-12 h-px bg-gradient-to-r from-transparent via-sahifa-500/30 to-transparent mx-auto" />
        <p className="text-[11px] text-slate-400 dark:text-slate-500 tracking-wide font-medium">
          @Sahifalab_hub_bot
        </p>
        <p className="text-[10px] text-slate-500 dark:text-slate-600">
          Powered by SAHIFALAB · 2026
        </p>
        <p className="text-[10px]">
          <a
            href="/terms"
            className="text-slate-400 dark:text-slate-500 hover:text-sahifa-500 transition-colors"
          >
            Foydalanish shartlari
          </a>
        </p>
      </motion.footer>
    </main>
  )
}

// Handles Telegram BackButton for the whole app (Telegram mode only)
const TelegramBackButtonHandler: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const isHome = location.pathname === '/'
  const handleBack = useCallback(() => navigate(-1), [navigate])
  useTelegramBackButton(isHome, handleBack)
  return null
}

// Route guard: only allows admin role through
const AdminRoute: React.FC = () => {
  const { user, isLoading } = useAuth()

  if (isLoading) return null
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'admin') return <Navigate to="/" replace />

  return <AdminPage />
}

// 404 page
const NotFoundPage: React.FC = () => {
  const navigate = useNavigate()
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-5 px-6 bg-[#FAFAFA] dark:bg-slate-950">
      <div className="w-20 h-20 rounded-[28px] bg-sahifa-500/10 text-sahifa-500 flex items-center justify-center">
        <BookOpenIcon className="w-9 h-9" />
      </div>
      <div className="text-center space-y-1">
        <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white">404</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Bu sahifa mavjud emas</p>
      </div>
      <button
        onClick={() => navigate('/', { replace: true })}
        className="px-6 py-2.5 bg-sahifa-500 hover:bg-sahifa-600 text-white text-sm font-semibold rounded-xl transition-colors"
      >
        Bosh sahifaga qaytish
      </button>
    </div>
  )
}

// Root route: Telegram mini app → simple MenuGrid home; web → social feed
const RootRoute: React.FC = () => {
  const { isTelegram } = usePlatform()
  if (isTelegram) return <HomePage />
  return <AppLayout rightSidebar={<FeedRightSidebar />} maxWidth="max-w-[680px]"><SocialFeed /></AppLayout>
}

// All app routes — shared between both layout modes.
// No AnimatePresence/motion.div wrapper — that was causing full-tree unmount/remount
// on every navigation (the root cause of blinking). The layout shell (WebLayout/
// TelegramLayout) is stable; only the page content swaps via React Router.
const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        {/* ── Auth pages (public, no layout shell) ───────────────── */}
        <Route path="/login"                  element={<LoginPage />} />
        <Route path="/auth/verify-pending"    element={<VerifyEmailPendingPage />} />
        <Route path="/auth/verify-email"      element={<VerifyEmailPage />} />
        <Route path="/auth/forgot-password"   element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password"    element={<ResetPasswordPage />} />

        {/* ── Fully public — guests welcome, actions guarded in-component ── */}
        <Route path="/"                element={<RootRoute />} />
        {/* Nav aliases — new canonical paths */}
        <Route path="/feed"            element={<AppLayout rightSidebar={<FeedRightSidebar />} maxWidth="max-w-[680px]"><SocialFeed /></AppLayout>} />
        <Route path="/certificate/:share_token" element={<CertificatePage />} />
        {/* Legacy paths kept for backward compat */}
        <Route path="/social"          element={<Navigate to="/feed" replace />} />
        <Route path="/discover"        element={<DiscoverUsers />} />
        <Route path="/search"          element={<SearchPage />} />
        <Route path="/courses"         element={<AppLayout rightSidebar={<CoursesRightSidebar />} maxWidth="max-w-[900px]"><CoursesPage /></AppLayout>} />
        <Route path="/courses/:id"     element={<CourseDetailPage />} />
        <Route path="/profile/:userId" element={<AppLayout rightSidebar={<ProfileRightSidebar />} maxWidth="max-w-[760px]"><PublicProfile /></AppLayout>} />
        <Route path="/teachers"        element={<TeachersGalleryPage />} />
        <Route path="/about"           element={<AboutPage />} />
        <Route path="/qollab-quvvatlash" element={<DonationPage />} />
        <Route path="/terms"           element={<TermsPage />} />
        <Route path="/privacy"         element={<PrivacyPage />} />
        <Route path="/delete-account"  element={<DeleteAccountPage />} />
        <Route path="/leaderboard"     element={<AppLayout maxWidth="max-w-[900px]"><LeaderboardPage /></AppLayout>} />

        {/* ── Protected — AuthGuard redirects guests to /login ──────── */}
        <Route element={<AuthGuard />}>
          <Route path="/network"         element={<AppLayout rightSidebar={<NetworkRightSidebar />} maxWidth="max-w-[760px]"><NetworkPage /></AppLayout>} />
          <Route path="/jobs"            element={<AppLayout rightSidebar={<JobsRightSidebar />} maxWidth="max-w-[760px]"><JobsPage /></AppLayout>} />
          <Route path="/flashcards"                element={<FlashcardsPage />} />
          <Route path="/flashcards/new"             element={<FlashcardCreatePage />} />
          <Route path="/flashcards/public/:deckId"  element={<FlashcardPublicDetailPage />} />
          <Route path="/flashcards/:deckId"         element={<FlashcardDeckPage />} />
          <Route path="/flashcards/:deckId/study"   element={<FlashcardStudyPage />} />
          <Route path="/study"          element={<Navigate to="/workspace?tab=focus" replace />} />
          <Route path="/workspace"      element={<AppLayout rightSidebar={<WorkspaceRightSidebar />} maxWidth="max-w-[760px]"><WorkspacePage /></AppLayout>} />
          <Route path="/read/:bookId"   element={<BookReaderPage />} />
          <Route path="/5-savol"        element={<AppLayout rightSidebar={<DailyQuizRightSidebar />} maxWidth="max-w-[680px]"><DailyQuizPage /></AppLayout>} />
          <Route path="/kitoblar"       element={<AppLayout maxWidth="max-w-[900px]"><KitoblarPage /></AppLayout>} />
          <Route path="/kitoblar/:id"   element={<BookDetailPage />} />
          <Route path="/resources"      element={<ResourcesPage />} />
          <Route path="/admin"          element={<AdminRoute />} />
          <Route path="/cabinet"        element={<Navigate to="/profile/me" replace />} />
          <Route path="/settings"       element={<AppLayout><SettingsPage /></AppLayout>} />
          {/* AI features temporarily disabled */}
          <Route path="/book-summarizer" element={<Navigate to="/" replace />} />
          <Route path="/ai-companion"   element={<Navigate to="/" replace />} />
          <Route path="/notifications"  element={<NotificationsPage />} />
          <Route path="/saved"           element={<AppLayout maxWidth="max-w-[680px]"><SavedPostsPage /></AppLayout>} />

          {/* Legacy teacher profile → redirects to unified profile */}
          <Route path="/teacher/:id" element={<TeacherRedirect />} />

          {/* Teacher & Admin role-gated routes */}
          <Route element={<RoleGuard roles={['teacher', 'admin']} />}>
            <Route path="/teacher"              element={<TeacherDashboardPage />} />
            <Route path="/teacher/setup"        element={<TeacherProfileSetupPage />} />
            <Route path="/teacher/wallet"       element={<TeacherWalletPage />} />
            <Route path="/courses/create"       element={<CourseCreatePage />} />
            <Route path="/courses/:id/edit"     element={<CourseCreatePage />} />
            <Route path="/courses/:courseId/lessons/add"                    element={<LessonCreatePage />} />
            <Route path="/courses/:courseId/lessons/:lessonId/edit"         element={<LessonCreatePage />} />
          </Route>

          {/* Admin payout management */}
          <Route path="/admin/payouts" element={<AdminPayoutsPage />} />

          {/* "5 Savol" daily quiz approval queue (090_daily_quiz) */}
          <Route path="/admin/daily-quiz" element={<AdminDailyQuizPage />} />
          <Route path="/admin/curated-facts" element={<AdminCuratedFactsPage />} />

          {/* Teacher application — any authenticated user */}
          <Route path="/become-teacher" element={<AppLayout maxWidth="max-w-[680px]"><TeacherApplyPage /></AppLayout>} />
        </Route>

        {/* 404 — catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}

/**
 * MaintenanceBanner — shows a dismissible Uzbek warning until 11-April 2026.
 * Auto-hidden after the deadline; dismissed state stored in sessionStorage.
 */
const MAINTENANCE_END = new Date('2026-04-11T00:00:00+05:00') // UTC+5 Tashkent

const MaintenanceBanner: React.FC = () => {
  const [visible, setVisible] = React.useState(() => {
    if (new Date() >= MAINTENANCE_END) return false
    return sessionStorage.getItem('maintenance_dismissed') !== '1'
  })

  if (!visible) return null

  return (
    <div className="relative z-[9999] w-full bg-amber-400 dark:bg-amber-500 text-amber-950 dark:text-amber-950">
      <div className="max-w-3xl mx-auto flex items-start gap-3 px-4 py-3 text-sm">
        <span className="text-xl leading-none mt-0.5" aria-hidden>🚧</span>
        <div className="flex-1 leading-snug">
          <span className="font-bold">Texnik ishlar davom etmoqda.</span>{' '}
          Platforma <span className="font-semibold">11-aprelgacha</span> yangilanish
          jarayonida — ba'zi bo'limlar vaqtincha cheklangan bo'lishi mumkin.
          Noqulaylik uchun uzr so'raymiz 🙏
        </div>
        <button
          onClick={() => { sessionStorage.setItem('maintenance_dismissed', '1'); setVisible(false) }}
          className="flex-shrink-0 ml-1 p-0.5 rounded hover:bg-amber-500/40 dark:hover:bg-amber-600/40 transition-colors"
          aria-label="Yopish"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  )
}

/**
 * AppShell — picks TelegramLayout or WebLayout based on detected platform.
 *
 * Telegram Mini App:  TelegramLayout → same behavior as before (no change)
 * Normal browser:     WebLayout → desktop sidebar + mobile bottom nav
 *
 * GA4 page view tracking runs in BOTH modes from a single place here.
 */
const AppShell: React.FC = () => {
  const { isTelegram } = usePlatform()
  const location = useLocation()

  // Google Analytics — track page views on every route change (both modes)
  useEffect(() => {
    const g = (window as any).gtag
    if (typeof g === 'function') {
      g('event', 'page_view', { page_path: location.pathname })
    }
  }, [location.pathname])

  if (isTelegram) {
    return (
      <TelegramLayout>
        <MaintenanceBanner />
        <TelegramBackButtonHandler />
        <GlobalProgressBar />
        <AppRoutes />
      </TelegramLayout>
    )
  }

  return (
    <WebLayout>
      <MaintenanceBanner />
      <AppRoutes />
    </WebLayout>
  )
}

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <ProgressProvider>
            <AppShell />
            <NotificationToast />
            <EmailLinkPrompt />
            <AppDownloadModal />
          </ProgressProvider>
        </AuthProvider>
        <ToastContainer />
      </Router>
    </ErrorBoundary>
  )
}

export default App
